import { db } from '../firebase';
import {
    collection,
    addDoc,
    getDocs,
    getDoc,
    doc,
    setDoc,
    updateDoc,
    deleteDoc,
    query,
    where,
    orderBy,
    serverTimestamp,
    runTransaction
} from 'firebase/firestore';
import { COLLECTION_REFERRALS } from '../shared/constants';

/**
 * Get all referrals
 * @returns {Promise<Array>} Array of referral objects
 */
export async function getAllReferrals() {
    try {
        const q = query(collection(db, COLLECTION_REFERRALS), orderBy('createdAt', 'desc'));
        const querySnapshot = await getDocs(q);
        return querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        }));
    } catch (error) {
        console.error('Error getting all referrals:', error);
        throw error;
    }
}

/**
 * Create a new referral (manual creation by admin/coordinator)
 * @param {Object} referralData 
 * @returns {Promise<string>} Created referral ID
 */
export async function createReferral(referralData) {
    try {
        const counterRef = doc(db, 'meta', 'referralCounter');
        const referralsRef = collection(db, COLLECTION_REFERRALS);
        let newDocId = '';
        let serialNumber = 0;

        await runTransaction(db, async (transaction) => {
            const counterSnap = await transaction.get(counterRef);
            serialNumber = (counterSnap.exists() ? (counterSnap.data()?.lastSerial ?? 0) : 0) + 1;

            const newRef = doc(referralsRef);
            newDocId = newRef.id;

            transaction.set(counterRef, { lastSerial: serialNumber }, { merge: true });
            transaction.set(newRef, {
                ...referralData,
                serialNumber,
                status: 'pending',
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
            });
        });

        return newDocId;
    } catch (error) {
        console.error('Error creating referral:', error);
        throw error;
    }
}

/**
 * Get referrals filtered by multiple guilds (either from or to)
 * Optimized for coordinators to avoid perm issues with getAllReferrals
 * @param {Array<string>} guildIds 
 * @returns {Promise<Array>}
 */
export async function getReferralsByMultipleGuilds(guildIds) {
    if (!guildIds || guildIds.length === 0) return [];

    try {
        // We do two queries (one for 'from', one for 'to') because Firestore doesn't support complex OR across fields easily
        const qFrom = query(
            collection(db, COLLECTION_REFERRALS),
            where('fromGuildId', 'in', guildIds),
            orderBy('createdAt', 'desc')
        );
        const qTo = query(
            collection(db, COLLECTION_REFERRALS),
            where('toGuildId', 'in', guildIds),
            orderBy('createdAt', 'desc')
        );

        const [snapFrom, snapTo] = await Promise.all([getDocs(qFrom), getDocs(qTo)]);

        const resultsMap = new Map();

        snapFrom.docs.forEach(doc => resultsMap.set(doc.id, { id: doc.id, ...doc.data() }));
        snapTo.docs.forEach(doc => resultsMap.set(doc.id, { id: doc.id, ...doc.data() }));

        return Array.from(resultsMap.values()).sort((a, b) => b.createdAt?.toMillis() - a.createdAt?.toMillis());
    } catch (error) {
        console.error('Error getting referrals by multiple guilds:', error);
        throw error;
    }
}

/**
 * Get referrals filtered by guild (either from or to)
 * @param {string} guildId 
 * @returns {Promise<Array>}
 */
export async function getReferralsByGuild(guildId) {
    return getReferralsByMultipleGuilds([guildId]);
}

/**
 * Get referral by ID
 * @param {string} id 
 * @returns {Promise<Object>} Referral object
 */
export async function getReferralById(id) {
    try {
        const docRef = doc(db, COLLECTION_REFERRALS, id);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
            return { id: docSnap.id, ...docSnap.data() };
        }
        return null;
    } catch (error) {
        console.error('Error getting referral by ID:', error);
        throw error;
    }
}

/**
 * Update referral details
 * @param {string} id 
 * @param {Object} updates 
 */
export async function updateReferral(id, updates) {
    try {
        const docRef = doc(db, COLLECTION_REFERRALS, id);
        await updateDoc(docRef, {
            ...updates,
            updatedAt: serverTimestamp()
        });
    } catch (error) {
        console.error('Error updating referral:', error);
        throw error;
    }
}

/**
 * Delete referral
 * @param {string} id 
 */
export async function deleteReferral(id) {
    try {
        const docRef = doc(db, COLLECTION_REFERRALS, id);
        await deleteDoc(docRef);
    } catch (error) {
        console.error('Error deleting referral:', error);
        throw error;
    }
}
